/**
 * StripeConnectCard — the "Accept payments from your clients" card under
 * Settings → Payments.
 *
 * Three states:
 *  1. NOT CONNECTED → "Connect Stripe to accept payments" CTA → redirects
 *     to Stripe's hosted Express onboarding.
 *  2. PENDING (account created but onboarding incomplete) → shows
 *     requirements left + "Continue setup" button.
 *  3. ACTIVE (charges_enabled=true) → shows ✓ Connected + payout last4 +
 *     "Manage in Stripe" SSO link + "Disconnect" option.
 *
 * Resilient to redirects: when the user returns from Stripe, the URL has
 * ?tab=payments&stripe_connected=1, so we poll status immediately to show
 * the freshest state without waiting for the webhook.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  ExternalLink,
  CreditCard,
  Sparkles,
  Unlink,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StripeConnectCard({ currentWorkspace }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadStatus = async () => {
    if (!currentWorkspace?.workspace_id) return;
    try {
      const r = await apiFetch(
        `${API_URL}/api/stripe-connect/status?workspace_id=${currentWorkspace.workspace_id}`,
      );
      if (r.ok) setStatus(await r.json());
    } catch (e) {
      console.error('Stripe status load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // If the user just returned from Stripe onboarding, the URL param is
    // set — refresh status + greet them with a toast + clear the param so
    // a reload doesn't re-trigger the toast.
    if (searchParams.get('stripe_connected') === '1') {
      toast.success('Welcome back from Stripe! Checking your account status...');
      searchParams.delete('stripe_connected');
      setSearchParams(searchParams, { replace: true });
    }
    if (searchParams.get('stripe_refresh') === '1') {
      toast.info('Your Stripe onboarding session expired. Click "Continue setup" to resume.');
      searchParams.delete('stripe_refresh');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [currentWorkspace?.workspace_id]);

  const handleOnboard = async () => {
    setActing(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/stripe-connect/onboard?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' },
      );
      const data = await r.json();
      if (!r.ok || !data.url) {
        toast.error(data.detail || 'Could not start Stripe onboarding');
        return;
      }
      // Hard redirect — Stripe's onboarding lives on stripe.com and we
      // can't embed it in an iframe per their CSP rules.
      window.location.href = data.url;
    } catch (e) {
      toast.error('Stripe onboarding request failed');
    } finally {
      setActing(false);
    }
  };

  const handleDashboardLogin = async () => {
    setActing(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/stripe-connect/dashboard-login?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' },
      );
      const data = await r.json();
      if (!r.ok || !data.url) {
        toast.error(data.detail || 'Could not open Stripe dashboard');
        return;
      }
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error('Could not generate dashboard link');
    } finally {
      setActing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(
      'Disconnect Stripe? New invoices will no longer have pay links. Your existing Stripe account and payment history are untouched — you can reconnect any time.',
    )) return;
    setActing(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/stripe-connect/disconnect?workspace_id=${currentWorkspace.workspace_id}`,
        { method: 'POST' },
      );
      if (r.ok) {
        toast.success('Stripe disconnected');
        setStatus({ connected: false, status: 'not_connected' });
      } else {
        toast.error('Disconnect failed');
      }
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Checking your payment setup...</span>
      </div>
    );
  }

  const connected = status?.connected && status?.status === 'active';
  const pending = status?.connected && status?.status !== 'active';

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="stripe-connect-card">
      {/* Header */}
      <div className="p-6 border-b border-border bg-gradient-to-r from-violet-500/5 to-emerald-500/5">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-violet-500/10">
            <CreditCard className="w-6 h-6 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold">Accept payments from your clients</h3>
              {connected && (
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Connected
                </Badge>
              )}
              {pending && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30 gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Setup incomplete
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Connect your Stripe account so your clients can pay invoices with a credit card or
              ACH bank transfer in one click. Funds land directly in your bank account
              — usually within 2 business days.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              <span><strong className="text-foreground">3.5% + $0.30</strong> per card payment</span>
              <span className="text-zinc-400">·</span>
              <span><strong className="text-foreground">0.8% (max $5)</strong> per ACH payment</span>
              <span className="text-zinc-400">·</span>
              <span>No monthly fees, no setup fees</span>
            </div>
          </div>
        </div>
      </div>

      {/* Body — varies by state */}
      <div className="p-6 space-y-4">
        {!status?.connected && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>5-minute Stripe-hosted onboarding</span>
              </div>
              <div className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Co-branded pay page with your logo</span>
              </div>
              <div className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>Auto-mark invoices paid on success</span>
              </div>
            </div>
            <Button
              onClick={handleOnboard}
              disabled={acting}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="stripe-connect-onboard-btn"
            >
              {acting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...</>
              ) : (
                <><CreditCard className="w-4 h-4 mr-2" /> Connect Stripe to accept payments</>
              )}
            </Button>
            <p className="text-[11px] text-muted-foreground">
              You'll be redirected to Stripe to enter your business info and bank account.
              CountBucks never sees or stores your sensitive details.
            </p>
          </div>
        )}

        {pending && (
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
              <p className="font-medium text-amber-700 dark:text-amber-300 mb-1">
                Stripe needs more information
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Your account is created but charges aren't enabled yet. Stripe still needs:
              </p>
              {status?.requirements_due?.length > 0 && (
                <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                  {status.requirements_due.slice(0, 5).map((req) => (
                    <li key={req}><code>{req.replace(/_/g, ' ')}</code></li>
                  ))}
                </ul>
              )}
            </div>
            <Button
              onClick={handleOnboard}
              disabled={acting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="stripe-connect-continue-btn"
            >
              {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
              Continue setup at Stripe
            </Button>
          </div>
        )}

        {connected && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Business name</div>
                <div className="font-medium truncate">{status.business_name || currentWorkspace?.name}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Charges</div>
                <div className="font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Enabled
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Payouts</div>
                <div className="font-medium">
                  {status.payouts_enabled ? (
                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {status.payout_last4 ? `to •••• ${status.payout_last4}` : 'Enabled'}
                    </span>
                  ) : (
                    <span className="text-amber-600 dark:text-amber-400">Pending</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleDashboardLogin}
                disabled={acting}
                data-testid="stripe-dashboard-btn"
              >
                {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Manage in Stripe
              </Button>
              <Button
                variant="ghost"
                onClick={handleDisconnect}
                disabled={acting}
                className="text-muted-foreground hover:text-rose-600"
                data-testid="stripe-disconnect-btn"
              >
                <Unlink className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
